// SsoProvider.js — wires react-oidc-context to the Cognito config.
//
// Wraps the app so components can start a Microsoft sign-in and read the
// resulting session. It does nothing on its own: no redirect happens until
// something calls signinRedirect().
//
// Spec: developer-tool/.claude/plans/frontend-sso-handoff.md
//
// TARGET PATH: src/shared/auth/SsoProvider.js

import React from 'react';
import { AuthProvider } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import authConfig, { isConfigured, redirectUri, logoutUri } from '../config/authConfig';

// ============================================
// Why the endpoints are declared instead of discovered
// ============================================
// Passing `metadata` stops oidc-client-ts fetching
// <issuer>/.well-known/openid-configuration. Two reasons:
//
//   1. Cognito's discovery document advertises endpoints on the pool's own
//      domain, which is not necessarily the hosted-UI domain the app is
//      registered against. The handoff gives the exact endpoints to use, so
//      using them directly removes a class of mismatch rather than trusting
//      discovery to agree.
//   2. It removes a network round-trip before the first redirect.
//
// `end_session_endpoint` is deliberately ABSENT. Cognito's /logout is not
// OIDC-standard — it takes client_id and logout_uri rather than id_token_hint
// and post_logout_redirect_uri — so signoutRedirect() would build a URL Cognito
// rejects. Logout is done explicitly in Stage 4 instead.
//
// `jwks_uri` is also absent, on purpose. The handoff is explicit: do not verify
// the token in the browser, no signature checks, no JWKS fetch. Claims are read
// for display only and the token is otherwise opaque; verification is the
// backend's job and it already does it properly.
const metadataFor = () => ({
  issuer: authConfig.issuer,
  authorization_endpoint: authConfig.authorizeUrl,
  token_endpoint: authConfig.tokenUrl,
});

// ============================================
// Settings
// ============================================
export const buildOidcSettings = () => ({
  authority: authConfig.issuer,
  client_id: authConfig.clientId,
  metadata: metadataFor(),

  // Derived, never configured — see authConfig. Byte-identical between the
  // authorize call and the token exchange by construction, which is what stops
  // redirect_uri_mismatch.
  redirect_uri: redirectUri(),
  post_logout_redirect_uri: logoutUri(),

  // Authorization code + PKCE. There is NO client secret: the app client is
  // deliberately secret-less and PKCE is what protects the exchange. A snippet
  // that sends a secret or a Basic auth header is for a confidential client and
  // does not apply here.
  response_type: 'code',
  scope: 'openid email profile',

  // Only one identity provider is configured, so the hosted page would show a
  // single "TonicMicrosoftIdp" button and no password form. Naming it here skips
  // that page entirely and goes straight to Microsoft.
  //
  // prompt=select_account makes Microsoft show its account picker instead of
  // silently reusing whichever account the browser is already signed in to. On a
  // shared or multi-account machine that silence is a trap: you get signed in as
  // someone else with no indication it happened. One extra click buys the
  // certainty of knowing who you are, and the ability to switch.
  extraQueryParams: {
    identity_provider: authConfig.identityProvider,
    prompt: 'select_account',
  },

  // Decision 2: sessionStorage, not localStorage. Tab-scoped, so the 30-day
  // refresh token exists only while the tab is open. The usual objection —
  // users hate re-authenticating — mostly does not apply, because Cognito
  // logout ends the Cognito session and not the Microsoft one, so re-entry is a
  // redirect that completes silently rather than a password prompt.
  //
  // Both stores are set: userStore holds the session, stateStore holds the PKCE
  // verifier and `state` between the redirect and the callback. Leaving
  // stateStore on the default localStorage would put the verifier somewhere
  // longer-lived than the session it protects.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),

  // Refresh BEFORE expiry, not in response to a 401. The handoff is explicit
  // that a 401 mid-request loses whatever the user was doing. With a refresh
  // token present, oidc-client-ts renews using it rather than a hidden iframe,
  // which matters because Cognito's hosted UI does not reliably support
  // prompt=none in an iframe.
  automaticSilentRenew: true,

  // Tokens last 60 minutes. Five minutes of margin is enough to absorb a slow
  // network without renewing so often that it is pointless.
  accessTokenExpiringNotificationTimeInSeconds: 300,

  // Drop ?code=&state= from the address bar once the exchange succeeds.
  //
  // This is not cosmetic. A code is single-use, so leaving it in the URL means a
  // refresh or a shared link retries a consumed code and fails with
  // invalid_grant — which reads like a broken login rather than a reused code.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
});

/**
 * Provides SSO to the tree when Cognito is configured, and gets out of the way
 * when it is not.
 *
 * The conditional is load-bearing rather than defensive. oidc-client-ts throws
 * from its own constructor when `authority` or `client_id` is missing, so
 * mounting AuthProvider in an environment without a pool would crash the whole
 * app at startup — taking username/password login down with it, which is the one
 * thing those environments still have. prodops is configured exactly that way
 * on purpose (`cognito_enabled = false`, "stays false until the UI ships").
 */
function SsoProvider({ children }) {
  if (!isConfigured()) return children;

  return <AuthProvider {...buildOidcSettings()}>{children}</AuthProvider>;
}

export default SsoProvider;
